import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { NATIVE_APP_SCHEME } from '../../config';
import { XIMI_NATIVE_POST_LOGOUT_URI, ximiOidcClient } from './oidcConfig';
import { XimiAuthSession } from './ximiAuthSession';

const IOS_PLATFORM = 'ios';

const isEphemeralAuthSession = (): boolean => Capacitor.getPlatform() === IOS_PLATFORM;

/**
 * Native only. Opens the SSO `/authorize` URL in the system browser (RFC 8252) — the app
 * never sees the SSO page nor what is typed in it.
 * - iOS: ephemeral ASWebAuthenticationSession → resolves with the callback URL, null if cancelled.
 * - Android: Custom Tab → resolves null at once; the OS hands the callback back to the app
 *   as a deeplink (useXimiDeeplink → signinCallback), even after a process death.
 */
export async function openXimiAuthBrowser(url: string): Promise<string | null> {
    if (isEphemeralAuthSession()) {
        const result = await XimiAuthSession.start({ url, callbackScheme: NATIVE_APP_SCHEME });
        return result?.url ?? null;
    }
    await Browser.open({ url });
    return null;
}

/**
 * Native only. Ends the SSO session the system browser keeps. iOS: nothing to do (ephemeral,
 * no cookie). Android: end_session in a Custom Tab, which clears Chrome's SSO cookie and comes
 * back to the app through XIMI_NATIVE_POST_LOGOUT_URI (ignored by useXimiDeeplink).
 */
export async function endXimiBrowserSsoSession(idTokenHint: string | undefined): Promise<void> {
    if (isEphemeralAuthSession()) return;
    const request = await ximiOidcClient.createSignoutRequest({
        id_token_hint: idTokenHint,
        post_logout_redirect_uri: XIMI_NATIVE_POST_LOGOUT_URI
    });
    await Browser.open({ url: request.url });
}
